import { queryOptions, useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'

import { ApiCaller } from '@graphit/callers'
import type { Label, Node, Relationship } from '@graphit/types'

import type { DeepPartial } from 'typeorm'

export const workspaceQueryOptions = (workspaceId: string) =>
	queryOptions({
		queryKey: ['workspace', workspaceId],
		queryFn: () => ApiCaller.getWorkspace(workspaceId)
	})

export const useWorkspaceQuery = (workspaceId: string) => useSuspenseQuery(workspaceQueryOptions(workspaceId))

export const labelNodesQueryOptions = (workspaceId: string, labelId: string) =>
	queryOptions({
		queryKey: ['workspace', workspaceId, 'label', labelId, 'nodes'],
		queryFn: () => ApiCaller.getLabelNodes(workspaceId, labelId)
	})

export const useLabelNodesQuery = (workspaceId: string, labelId: string) =>
	useSuspenseQuery(labelNodesQueryOptions(workspaceId, labelId))

export const workspaceLabelsQueryOptions = (workspaceId: string) =>
	queryOptions({
		queryKey: ['workspace', workspaceId, 'labels'],
		queryFn: () => ApiCaller.getLabels(workspaceId)
	})

export const useWorkspaceLabelsQuery = (workspaceId: string, enabled: boolean) =>
	useQuery({ ...workspaceLabelsQueryOptions(workspaceId), enabled })

export const useCreateLabelMutation = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (labelData: DeepPartial<Label>) => ApiCaller.createLabel(labelData),
		onSuccess: (_, variables) => {
			if (variables.workspaceId) {
				queryClient.invalidateQueries({ queryKey: ['workspace', variables.workspaceId] })
				queryClient.invalidateQueries({ queryKey: ['workspace', variables.workspaceId, 'labels'] })
				queryClient.invalidateQueries({ queryKey: ['cartography'] })
			}
		}
	})
}

export const useUpdateLabelMutation = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ id, labelData }: { id: string; workspaceId: string; labelData: DeepPartial<Label> }) =>
			ApiCaller.updateLabel(id, labelData),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ['workspace', variables.workspaceId] })
			queryClient.invalidateQueries({ queryKey: ['workspace', variables.workspaceId, 'labels'] })
			queryClient.invalidateQueries({ queryKey: ['cartography'] })
		}
	})
}

export const useDeleteLabelMutation = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ id }: { id: string; workspaceId: string }) => ApiCaller.deleteLabel(id),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ['workspace', variables.workspaceId] })
			queryClient.invalidateQueries({ queryKey: ['workspace', variables.workspaceId, 'labels'] })
			queryClient.removeQueries({ queryKey: ['workspace', variables.workspaceId, 'label', variables.id, 'nodes'] })
			queryClient.invalidateQueries({ queryKey: ['relationships'] })
			queryClient.invalidateQueries({ queryKey: ['types', 'stats'] })
			queryClient.invalidateQueries({ queryKey: ['cartography'] })
		}
	})
}

export const useCreateNodeMutation = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ workspaceId, node }: { workspaceId: string; node: Partial<Node> }) =>
			ApiCaller.createNode(workspaceId, node),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ['workspace', variables.workspaceId] })
			if (variables.node.labelId) {
				queryClient.invalidateQueries({
					queryKey: ['workspace', variables.workspaceId, 'label', variables.node.labelId, 'nodes']
				})
			}
			queryClient.invalidateQueries({ queryKey: ['cartography'] })
		}
	})
}

export const useUpdateNodeMutation = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({
			id,
			node
		}: {
			id: string
			workspaceId: string
			labelId: string
			previousLabelId?: string | undefined
			node: Partial<Node>
		}) => ApiCaller.updateNode(id, node),
		onSuccess: (_, variables) => {
			if (variables.previousLabelId && variables.previousLabelId !== variables.labelId) {
				queryClient.invalidateQueries({ queryKey: ['workspace', variables.workspaceId] })
				queryClient.invalidateQueries({
					queryKey: ['workspace', variables.workspaceId, 'label', variables.previousLabelId, 'nodes']
				})
			}

			queryClient.invalidateQueries({
				queryKey: ['workspace', variables.workspaceId, 'label', variables.labelId, 'nodes']
			})
			queryClient.invalidateQueries({ queryKey: ['cartography'] })
		}
	})
}

export const useDeleteNodeMutation = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ id }: { id: string; workspaceId: string; labelId: string }) => ApiCaller.deleteNode(id),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ['workspace', variables.workspaceId] })
			queryClient.invalidateQueries({
				queryKey: ['workspace', variables.workspaceId, 'label', variables.labelId, 'nodes']
			})
			queryClient.invalidateQueries({ queryKey: ['relationships'] })
			queryClient.invalidateQueries({ queryKey: ['types', 'stats'] })
			queryClient.invalidateQueries({ queryKey: ['cartography'] })
		}
	})
}

export const useDeleteNodePropertyMutation = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ id, property }: { id: string; property: string; workspaceId: string; labelId: string }) =>
			ApiCaller.deleteNodeProperty(id, property),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: ['workspace', variables.workspaceId, 'label', variables.labelId, 'nodes']
			})
			queryClient.invalidateQueries({ queryKey: ['cartography'] })
		}
	})
}

export const useShareWorkspaceMutation = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ workspaceId, userId, shared }: { workspaceId: string; userId: string; shared: boolean }) =>
			ApiCaller.shareWorkspace(workspaceId, userId, shared),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ['workspace', variables.workspaceId] })
		}
	})
}

export const useSeedWorkspaceMutation = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ApiCaller.seedWorkspace,
		onSuccess: (_, workspaceId) => {
			queryClient.invalidateQueries({ queryKey: ['workspace', workspaceId] })
			queryClient.invalidateQueries({ queryKey: ['types'] })
			queryClient.invalidateQueries({ queryKey: ['types', 'stats'] })
		}
	})
}

export const useImportWorkspaceDataMutation = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({
			nodes,
			relationships,
			createCartography
		}: {
			workspaceId: string
			nodes: Node[]
			relationships: Relationship[]
			createCartography: boolean
		}) => ApiCaller.importData(nodes, relationships, createCartography),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ['workspaces'] })
			queryClient.invalidateQueries({ queryKey: ['workspace', variables.workspaceId] })
			queryClient.invalidateQueries({ queryKey: ['relationships'] })
			queryClient.invalidateQueries({ queryKey: ['types', 'stats'] })

			if (variables.createCartography) {
				queryClient.invalidateQueries({ queryKey: ['cartographies'] })
			}
		}
	})
}

export const nodesConflictsQueryOptions = (workspaceId: string, nodes: Node[]) =>
	queryOptions({
		queryKey: ['workspace', workspaceId, 'nodesConflicts', nodes.map(n => n.name)],
		queryFn: () => ApiCaller.getNodesConflicts(workspaceId, nodes)
	})

export const useNodesConflictsQuery = (workspaceId: string, nodes: Node[]) =>
	useQuery(nodesConflictsQueryOptions(workspaceId, nodes))
